import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import type { Source } from '@/domains/types'

export const sourceKeys = {
  byLead: (leadId: string) => ['source', 'lead', leadId] as const,
}

export function useGetSourceByLead(leadId: string) {
  return useQuery({
    queryKey: sourceKeys.byLead(leadId),
    queryFn: async (): Promise<Source | null> => {
      const response = await apiClient.get<Source | null>(`/leads/${leadId}/source`)
      return response.data
    },
  })
}

export function useUpsertSource(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      title: string
      description: string
      sourceUrl?: string | null
      imageUrl?: string | null
    }): Promise<Source> => {
      const response = await apiClient.post<Source>(`/leads/${leadId}/source`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.byLead(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteSource(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<boolean> => {
      const response = await apiClient.delete<boolean>(`/leads/${leadId}/source`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.byLead(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}
